/**
 * useLiveActivity.ts
 * Hook for polling live exam activity data (social proof).
 * V2: Fetches both active + recently completed sessions (7 days).
 * Cross-platform: includes both VSAT and THPT.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { apiService } from '../lib/api';

export interface LiveSession {
  maskedEmail: string;
  avatar?: string | null;
  examTitle: string;
  examId: string;
  type: 'ACTIVE' | 'COMPLETED';
  startedAt?: string;
  completedAt?: string;
  score?: number | null;
  totalScore?: number;
}

export interface LiveActivityData {
  totalActive: number;
  totalRecentCompleted: number;
  activeSessions: LiveSession[];
  recentCompletedSessions: LiveSession[];
}

/** Merged sessions with active first, then completed */
export interface MergedLiveActivity {
  /** Raw data from API */
  raw: LiveActivityData;
  /** Merged list: active first, then completed */
  allSessions: LiveSession[];
  /** Total count (active + completed) */
  totalCount: number;
  /** Whether there is any data to show */
  hasData: boolean;
}

interface UseLiveActivityOptions {
  /** Polling interval in ms (default: 30000 = 30s) */
  intervalMs?: number;
  /** Whether to enable polling (default: true) */
  enabled?: boolean;
}

export function useLiveActivity({
  intervalMs = 30_000,
  enabled = true,
}: UseLiveActivityOptions = {}): MergedLiveActivity | null {
  const [data, setData] = useState<LiveActivityData | null>(null);

  const fetchActivity = useCallback(async () => {
    try {
      const json = await apiService.getLiveActivity();
      setData(json);
    } catch {
      // Silently fail — social proof is non-critical
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;

    fetchActivity();
    const id = setInterval(fetchActivity, intervalMs);
    return () => clearInterval(id);
  }, [enabled, intervalMs, fetchActivity]);

  // Merge active + completed interleaved (1 active, 1 completed, ...)
  const merged = useMemo<MergedLiveActivity | null>(() => {
    if (!data) return null;

    const active = data.activeSessions || [];
    const completed = data.recentCompletedSessions || [];
    const allSessions: LiveSession[] = [];
    const maxLen = Math.max(active.length, completed.length);
    for (let i = 0; i < maxLen; i++) {
      if (i < active.length) allSessions.push(active[i]);
      if (i < completed.length) allSessions.push(completed[i]);
    }

    return {
      raw: data,
      allSessions,
      totalCount: data.totalActive + data.totalRecentCompleted,
      hasData: allSessions.length > 0,
    };
  }, [data]);

  return merged;
}
